import { useMemo } from 'react';
import {
  BarList, Card, Grid, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, Title,
} from '@tremor/react';
import { t } from '../lib/i18n';
import type { LostByLineRow } from '../api/types';

const fmtMxn = (n: number) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(n);
const fmtInt = (n: number) => new Intl.NumberFormat('es-MX').format(n);

/**
 * v2.56 — "Ventas perdidas por línea" for the selected person on /vendedores.
 * Lost sales = titm type "L" (a part the customer wanted that we couldn't
 * fill). Shows all three metrics per line — conteo, unidades, valor — plus a
 * value-by-line and count-by-line chart. Rows arrive already filtered to the
 * chosen person (or all). Line label = description when known, else the code.
 */
export default function LostByLineSection({ rows }: { rows: LostByLineRow[] }) {
  // Aggregate across the (possibly multi-person) rows by line, so the section
  // reads as one person's — or everyone's — lost sales per line.
  const byLine = useMemo(() => {
    const m = new Map<string, { code: string; label: string; count: number; units: number; value: number }>();
    for (const r of rows) {
      const g = m.get(r.line) ?? { code: r.line, label: r.line_desc?.trim() || r.line, count: 0, units: 0, value: 0 };
      g.count += r.count;
      g.units += r.units;
      g.value += r.value_mxn;
      m.set(r.line, g);
    }
    return [...m.values()].sort((a, b) => b.value - a.value);
  }, [rows]);

  const totals = useMemo(
    () => byLine.reduce((a, l) => ({ count: a.count + l.count, units: a.units + l.units, value: a.value + l.value }),
      { count: 0, units: 0, value: 0 }),
    [byLine],
  );

  const valueBars = useMemo(
    () => byLine.slice(0, 12).map((l) => ({ name: l.label, value: l.value })),
    [byLine],
  );
  const countBars = useMemo(
    () => [...byLine].sort((a, b) => b.count - a.count).slice(0, 12).map((l) => ({ name: l.label, value: l.count })),
    [byLine],
  );

  if (byLine.length === 0) {
    return (
      <Card>
        <Title className="text-base">{t.salespeople.lost.title}</Title>
        <Text className="mt-2">{t.salespeople.lost.empty}</Text>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Grid numItemsMd={2} className="gap-4">
        <Card>
          <Title className="text-base">{t.salespeople.lost.valueChart}</Title>
          <BarList data={valueBars} valueFormatter={fmtMxn} color="rose" className="mt-3" />
        </Card>
        <Card>
          <Title className="text-base">{t.salespeople.lost.countChart}</Title>
          <BarList data={countBars} valueFormatter={fmtInt} color="amber" className="mt-3" />
        </Card>
      </Grid>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.salespeople.lost.cols.line}</TableHeaderCell>
                <TableHeaderCell>{t.salespeople.lost.cols.desc}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.salespeople.lost.cols.count}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.salespeople.lost.cols.units}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.salespeople.lost.cols.value}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byLine.map((l) => (
                <TableRow key={l.code}>
                  <TableCell className="font-medium">{l.code}</TableCell>
                  <TableCell className="text-tremor-content">{l.label === l.code ? '' : l.label}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(l.count)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(l.units)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtMxn(l.value)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold border-t-2 border-tremor-border">
                <TableCell>{t.salespeople.lost.total}</TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">{fmtInt(totals.count)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtInt(totals.units)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtMxn(totals.value)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
